// Industry-standard frontend file validation layer.
// Purpose: catch obviously bad files fast (UX gate), not replace server-side scanning.
// True virus scanning and deep OCR must happen on the backend.

const ALLOWED_TYPES = [
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const MAX_SIZE_MB = 10;

// Allowed Excel file types for bulk upload
const ALLOWED_EXCEL_TYPES = [
    'application/vnd.ms-excel',           // .xls
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
];

// Magic byte signatures — extension can be faked, file content cannot.
const MAGIC_SIGNATURES = {
    'application/pdf': [[0x25, 0x50, 0x44, 0x46]],           // %PDF
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [
        [0x50, 0x4B, 0x03, 0x04],                             // PK (DOCX is a ZIP archive)
    ],
    'application/vnd.ms-excel': [
        [0xD0, 0xCF, 0x11, 0xE0],                            // D0 CF 11 E0 (old Excel .xls)
    ],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
        [0x50, 0x4B, 0x03, 0x04],                            // PK (XLSX is a ZIP archive)
    ],
};

async function checkMagicBytes(file) {
    const buffer = await file.slice(0, 8).arrayBuffer();
    const bytes = new Uint8Array(buffer);
    const sigs = MAGIC_SIGNATURES[file.type] ?? [];
    return sigs.some(sig => sig.every((byte, i) => bytes[i] === byte));
}

// Password-protected PDFs have an /Encrypt dictionary near the file header.
// LLMs cannot read encrypted PDFs, so we block them early.
async function isPdfEncrypted(file) {
    const buffer = await file.slice(0, 50 * 1024).arrayBuffer();
    const text = new TextDecoder('latin1').decode(buffer); // latin1 preserves raw byte values
    return /\/Encrypt\b/.test(text);
}

// Scanned (image-only) PDFs have no font definitions or text operators.
// Text-based PDFs always declare fonts in the uncompressed object catalog,
// even when the text streams themselves are compressed.
async function pdfHasTextLayer(file) {
    const buffer = await file.slice(0, 200 * 1024).arrayBuffer();
    const text = new TextDecoder('latin1').decode(buffer);
    const hasFont = /\/BaseFont\b/.test(text) || /\/Font[\s\r\n]*<</.test(text);
    const hasTextOp = /\bBT\b/.test(text); // Begin Text operator
    return hasFont || hasTextOp;
}

/**
 * Validates a file for security and OCR readiness.
 * Returns an error message string, or null if the file passes all checks.
 */
export async function validateUploadedFile(file) {
    if (!ALLOWED_TYPES.includes(file.type)) {
        return 'Only PDF and Word (.docx) files are allowed.';
    }
    if (file.size === 0) {
        return 'The file appears to be empty.';
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        return `File must be under ${MAX_SIZE_MB}MB.`;
    }

    // Magic bytes: confirm file content truly matches its declared type.
    const magicValid = await checkMagicBytes(file);
    if (!magicValid) {
        return 'File content does not match its extension. Please upload a valid PDF or Word document.';
    }

    // PDF-specific: encryption and text layer checks.
    if (file.type === 'application/pdf') {
        const encrypted = await isPdfEncrypted(file);
        if (encrypted) {
            return 'This PDF is password-protected. Please remove the password before uploading.';
        }
        const hasText = await pdfHasTextLayer(file);
        if (!hasText) {
            return 'This appears to be a scanned PDF with no readable text. Please upload a text-based PDF or a Word document instead.';
        }
    }

    return null;
}

/**
 * Validates an Excel file for bulk upload operations.
 * Returns an error message string, or null if the file passes all checks.
 * @param {File} file - The Excel file to validate
 * @returns {Promise<string|null>} Error message or null if valid
 */
export async function validateExcelFile(file, context = 'bulk upload') {
    if (!ALLOWED_EXCEL_TYPES.includes(file.type)) {
        return `Only Excel files (.xls, .xlsx) are allowed for ${context}.`;
    }
    if (file.size === 0) {
        return 'The file appears to be empty.';
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
        return `File must be under ${MAX_SIZE_MB}MB.`;
    }

    // Magic bytes: confirm file content truly matches its declared type.
    const magicValid = await checkMagicBytes(file);
    if (!magicValid) {
        return 'File content does not match its extension. Please upload a valid Excel file (.xls or .xlsx).';
    }

    return null;
}
